// Ajoute automatiquement l'en-tête X-Device-Id sur chaque requête (fetch ET
// axios) vers le backend (proxy-server). Contrepartie côté serveur :
// authMiddleware (proxy-server/middlewares/authMiddleware.js) exige cet
// en-tête et vérifie que l'appareil est "trusted" avant de laisser passer une
// route authentifiée — sans quoi un token Firebase volé suffisait à taper
// l'API directement en contournant la vérification par email (qui n'était
// qu'un verrou d'UI React).
//
// Patch global (fetch + axios par défaut) plutôt qu'un ajout dans chaque
// appel : des dizaines de services front construisent leurs propres headers
// (Bearer token inclus) sans passer par un client HTTP commun, certains en
// fetch, certains en axios (voir AIChatWindow.tsx, AcademyTemp.tsx — tous
// deux importent `axios` par défaut, sans instance custom, donc un seul
// interceptor global les couvre). Réécrire chaque call site aurait été une
// passe risquée sur des dizaines de fichiers pour un seul header ; les patchs
// sont locaux à ce module, n'affectent que les requêtes vers API_BASE_URL, et
// n'interfèrent pas avec les headers déjà posés par l'appelant.

import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import { getDeviceId } from '../services/deviceService';

const DEVICE_ID_HEADER = 'X-Device-Id';

declare global {
  interface Window {
    __deviceIdFetchInstalled?: boolean;
    __deviceIdAxiosInstalled?: boolean;
    __deviceIdXhrInstalled?: boolean;
  }
}

export function installDeviceIdFetchInterceptor(): void {
  if (typeof window === 'undefined' || typeof window.fetch !== 'function') return;
  if (window.__deviceIdFetchInstalled) return;
  window.__deviceIdFetchInstalled = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = ((input: RequestInfo | URL, init?: RequestInit) => {
    try {
      const url = typeof input === 'string'
        ? input
        : input instanceof URL
          ? input.toString()
          : input.url;

      if (url.startsWith(API_BASE_URL)) {
        const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined));
        headers.set(DEVICE_ID_HEADER, getDeviceId());
        return originalFetch(input, { ...init, headers });
      }
    } catch {
      // Ne bloque jamais une requête pour un souci d'instrumentation.
    }
    return originalFetch(input, init);
  }) as typeof window.fetch;
}

export function installDeviceIdAxiosInterceptor(): void {
  if (typeof window === 'undefined') return;
  if (window.__deviceIdAxiosInstalled) return;
  window.__deviceIdAxiosInstalled = true;

  axios.interceptors.request.use((config) => {
    try {
      const url = config.url
        ? new URL(config.url, config.baseURL || window.location.origin).toString()
        : '';
      if (url.startsWith(API_BASE_URL)) {
        config.headers = config.headers ?? {};
        (config.headers as Record<string, string>)[DEVICE_ID_HEADER] = getDeviceId();
      }
    } catch {
      // Ne bloque jamais une requête pour un souci d'instrumentation.
    }
    return config;
  });
}

export function installDeviceIdXhrInterceptor(): void {
  if (typeof window === 'undefined' || typeof XMLHttpRequest === 'undefined') return;
  if (window.__deviceIdXhrInstalled) return;
  window.__deviceIdXhrInstalled = true;

  const proto = XMLHttpRequest.prototype;
  // Ne PAS .bind(proto) ici : `open`/`setRequestHeader` natifs exigent que
  // `this` soit une vraie instance de XMLHttpRequest (slot interne). Lier la
  // fonction au prototype fige `this` sur le prototype lui-même, et l'appel
  // natif lève alors "TypeError: Illegal invocation" pour CHAQUE requête XHR
  // (axios en fait, y compris AIChatWindow.tsx). On garde donc les fonctions
  // non liées et on propage l'instance réelle via .call(this, ...).
  const originalOpen = proto.open;
  const originalSetRequestHeader = proto.setRequestHeader;
  const originalSend = proto.send;

  proto.open = function (
    this: XMLHttpRequest & { __deviceIdUrl?: string; __deviceIdHeaderSet?: boolean },
    method: string,
    url: string | URL,
    async?: boolean,
    username?: string | null,
    password?: string | null,
  ) {
    this.__deviceIdUrl = typeof url === 'string' ? url : url.toString();
    this.__deviceIdHeaderSet = false;
    return originalOpen.call(this, method, url, async ?? true, username ?? undefined, password ?? undefined);
  } as typeof proto.open;

  // Se contente d'observer si l'appelant (ex. l'intercepteur axios ci-dessus,
  // qui pose déjà X-Device-Id dans config.headers) pose LUI-MÊME cet en-tête —
  // sans jamais injecter ici. Injecter au premier setRequestHeader() rencontré
  // (peu importe son nom, ex. "Authorization" posé avant "X-Device-Id" par
  // axios) créait une course : l'injection partait AVANT qu'axios ne pose son
  // propre X-Device-Id, et setRequestHeader ne remplace pas une valeur déjà
  // posée pour le même nom, il CONCATÈNE ("uuid, uuid") — un deviceId ainsi
  // dédoublé ne correspond plus à aucun document Firestore → 403
  // DEVICE_NOT_TRUSTED même pour un appareil réellement trusted.
  proto.setRequestHeader = function (
    this: XMLHttpRequest & { __deviceIdUrl?: string; __deviceIdHeaderSet?: boolean },
    name: string,
    value: string,
  ) {
    originalSetRequestHeader.call(this, name, value);
    if (name.toLowerCase() === DEVICE_ID_HEADER.toLowerCase()) {
      this.__deviceIdHeaderSet = true;
    }
  } as typeof proto.setRequestHeader;

  // L'injection elle-même n'a lieu qu'ICI, à send() — donc APRÈS que tous les
  // setRequestHeader() de l'appelant ont déjà eu lieu (open → setRequestHeader×N
  // → send est le seul ordre possible de l'API XHR). On sait alors avec
  // certitude si X-Device-Id a déjà été posé ou non : plus de course possible.
  proto.send = function (
    this: XMLHttpRequest & { __deviceIdUrl?: string; __deviceIdHeaderSet?: boolean },
    ...args: [body?: Document | XMLHttpRequestBodyInit | null]
  ) {
    const url = this.__deviceIdUrl;
    if (url && url.startsWith(API_BASE_URL) && !this.__deviceIdHeaderSet) {
      this.__deviceIdHeaderSet = true;
      try {
        originalSetRequestHeader.call(this, DEVICE_ID_HEADER, getDeviceId());
      } catch {
        // Ne bloque jamais une requête pour un souci d'instrumentation.
      }
    }
    return originalSend.apply(this, args);
  } as typeof proto.send;
}
